import {
  CapabilityRegistry,
  FitToViewMeasurementCapabilityCoordinator,
  FitToViewModelCapabilityCoordinator,
  FitToViewOnloadCapabilityCoordinator,
  MarkupEscapeHandlerCoordinator,
} from '@trimble/web3d';
import {
  AlignmentMeasurementCapability,
  AngleMeasurementCapability,
  LineDetectionCapability,
  LineMeasurementCapability,
  MarkupCapability,
  ObjectPropertiesPanelCapability,
  PointCloudStylingCapability,
  PointMeasurementCapability,
  SnappingCapability,
} from '@trimble/web3d-capabilities';
import {
  getTrbModelId,
  MarkupsCapabilityProvider,
  MeasurementsCapabilityProvider,
  ObjectPropertiesProvider,
  PointCloudStylingCapabilityProvider,
  StationsCapabilityProvider,
  ViewsCapabilityProvider,
} from '@trimble/web3d-providers';
import { LoadState } from '@trimble/data-loading';
import { createContext, createEffect, JSX, useContext } from 'solid-js';
import { useConnectProject } from '@trimble/connect-project-providers';
import { useApiServices } from './ApiServiceProvider';
import { useDataContext } from './data-state/DataContextProvider';
import { getVisiblePointCloudIds } from './data-state/DataContextProviderHelpers';
import { createGetAlignmentMeasurement } from './utils/alignment-measurement';
import { MeasurementsClearingCapabilityCoordinator } from './coordinators/measurements/measurements-clearing-capability-coordinator';
import { PathSectionsCoordinator } from './coordinators/path-sections/path-sections-coordinator';
import { useProjectSettings } from './ProjectSettingsProvider';
import {
  CrossPaneFrustumsCoordinator,
  isMarkerEligiblePane,
  ViewsLoadingCoordinator,
} from '@mobile-mapping/web3d';
import {
  IssueMarkerIconCoordinator,
  IssueMarkerPickCoordinator,
} from '@trimble/tc-issue-management-web3d-utilities';
import {
  FrustumCapabilityCoordinator,
  MapStationSyncCoordinator,
  PathNavigationCoordinator,
  SelectionSyncCoordinator,
} from '@trimble/pane-sync';

/**
 * Context for sharing the CapabilityRegistry instance across the application.
 * This allows child components to access global providers, capabilities, and coordinators.
 */
const CapabilityRegistryContextProvider = createContext<
  CapabilityRegistry | undefined
>(undefined);

export interface CapabilityRegistryProps {
  children: JSX.Element;
}

export function CapabilityRegistryProvider(
  props: CapabilityRegistryProps
): JSX.Element {
  const { alignmentApi, scanDataApi } = useApiServices();
  const { selectedTenantTrn } = useConnectProject();
  const dataContext = useDataContext();
  const { formattingSettings, alignmentSettings } = useProjectSettings();

  const capabilityRegistry = new CapabilityRegistry();

  const measurementProvider = capabilityRegistry.register(
    MeasurementsCapabilityProvider,
    new MeasurementsCapabilityProvider(
      formattingSettings,
      undefined,
      alignmentSettings
    )
  );

  capabilityRegistry.register(
    SnappingCapability,
    new SnappingCapability(measurementProvider)
  );

  capabilityRegistry.register(
    PointMeasurementCapability,
    new PointMeasurementCapability(measurementProvider)
  );

  capabilityRegistry.register(
    LineMeasurementCapability,
    new LineMeasurementCapability(measurementProvider)
  );

  // Not supported in mobile mapping yet.
  // capabilityRegistry.register(
  //   PolylineMeasurementCapability,
  //   new PolylineMeasurementCapability(measurementProvider)
  // );

  capabilityRegistry.register(
    AngleMeasurementCapability,
    new AngleMeasurementCapability(measurementProvider)
  );

  capabilityRegistry.register(
    LineDetectionCapability,
    new LineDetectionCapability(measurementProvider)
  );

  capabilityRegistry.register(
    StationsCapabilityProvider,
    new StationsCapabilityProvider()
  );

  const markupsProvider = capabilityRegistry.register(
    MarkupsCapabilityProvider,
    new MarkupsCapabilityProvider()
  );

  capabilityRegistry.register(
    MarkupCapability,
    new MarkupCapability(markupsProvider)
  );

  const pointCloudStylingProvider = capabilityRegistry.register(
    PointCloudStylingCapabilityProvider,
    new PointCloudStylingCapabilityProvider(
      dataContext.classificationSchemes,
      dataContext.classStatistics
    )
  );

  const viewsProvider = capabilityRegistry.register(
    ViewsCapabilityProvider,
    new ViewsCapabilityProvider(
      [],
      measurementProvider,
      markupsProvider,
      pointCloudStylingProvider,
      scanDataApi,
      (ids) => dataContext.dataLoadingService.loadExclusive('model', ids),
      (ids) => dataContext.dataLoadingService.loadExclusive('pointCloud', ids)
    )
  );

  capabilityRegistry.register(
    PointCloudStylingCapability,
    new PointCloudStylingCapability(pointCloudStylingProvider)
  );

  const getAlignmentMeasurement = createGetAlignmentMeasurement(
    alignmentApi,
    selectedTenantTrn,
    () => dataContext.activeItems.alignmentId ?? undefined
  );

  capabilityRegistry.register(
    AlignmentMeasurementCapability,
    new AlignmentMeasurementCapability(
      measurementProvider,
      getAlignmentMeasurement
    )
  );

  capabilityRegistry.register(
    FitToViewModelCapabilityCoordinator,
    new FitToViewModelCapabilityCoordinator(capabilityRegistry)
  );

  const selectionSyncCoordinator = capabilityRegistry.register(
    SelectionSyncCoordinator,
    new SelectionSyncCoordinator(capabilityRegistry)
  );

  const objectPropertiesProvider = new ObjectPropertiesProvider(
    selectionSyncCoordinator.selectedByViewer
  );

  capabilityRegistry.register(
    ObjectPropertiesPanelCapability,
    new ObjectPropertiesPanelCapability(objectPropertiesProvider)
  );

  capabilityRegistry.register(
    FitToViewMeasurementCapabilityCoordinator,
    new FitToViewMeasurementCapabilityCoordinator(capabilityRegistry)
  );

  capabilityRegistry.register(
    FitToViewOnloadCapabilityCoordinator,
    new FitToViewOnloadCapabilityCoordinator(
      capabilityRegistry,
      dataContext.dataLoadingService
    )
  );

  capabilityRegistry.register(
    FrustumCapabilityCoordinator,
    new FrustumCapabilityCoordinator(capabilityRegistry)
  );

  capabilityRegistry.register(
    CrossPaneFrustumsCoordinator,
    new CrossPaneFrustumsCoordinator(capabilityRegistry)
  );

  capabilityRegistry.register(
    PathNavigationCoordinator,
    new PathNavigationCoordinator(capabilityRegistry)
  );

  capabilityRegistry.register(
    MapStationSyncCoordinator,
    new MapStationSyncCoordinator(capabilityRegistry)
  );

  capabilityRegistry.register(
    MarkupEscapeHandlerCoordinator,
    new MarkupEscapeHandlerCoordinator(capabilityRegistry)
  );

  capabilityRegistry.register(
    ViewsLoadingCoordinator,
    new ViewsLoadingCoordinator(capabilityRegistry, viewsProvider, () =>
      getVisiblePointCloudIds(dataContext.dataState)
    )
  );

  // Register PathSectionsCoordinator to manage mission path section highlighting
  capabilityRegistry.register(
    PathSectionsCoordinator,
    new PathSectionsCoordinator(capabilityRegistry, dataContext.dataState)
  );

  capabilityRegistry.register(
    MeasurementsClearingCapabilityCoordinator,
    new MeasurementsClearingCapabilityCoordinator(
      capabilityRegistry,
      dataContext.dataState
    )
  );

  capabilityRegistry.register(
    IssueMarkerIconCoordinator,
    new IssueMarkerIconCoordinator(capabilityRegistry, {
      isPaneEligible: isMarkerEligiblePane,
    })
  );

  capabilityRegistry.register(
    IssueMarkerPickCoordinator,
    new IssueMarkerPickCoordinator(capabilityRegistry, {
      isPaneEligible: isMarkerEligiblePane,
    })
  );

  createEffect(() => {
    const coord = capabilityRegistry.get(PathNavigationCoordinator)();
    const modelId = coord?.desiredAlignmentModelId();
    const normalized =
      modelId != null && modelId !== ''
        ? (getTrbModelId(modelId) ?? modelId)
        : null;
    dataContext.setActiveItems('alignmentId', normalized);
  });

  createEffect(() => {
    const stations = capabilityRegistry.get(StationsCapabilityProvider)();
    const runId = stations?.activeRunId() ?? null;
    let pointCloudTrn: string | null = null;
    const missions = dataContext.dataState.mission;
    if (runId != null && missions.state === LoadState.LoadComplete) {
      for (const mission of Object.values(missions.items)) {
        const run = mission.runs.find((r) => r.runId === runId);
        if (run) {
          pointCloudTrn = run.id;
          break;
        }
      }
    }
    dataContext.setActiveItems('pointCloudTrnId', pointCloudTrn);
  });

  return (
    <CapabilityRegistryContextProvider.Provider value={capabilityRegistry}>
      {props.children}
    </CapabilityRegistryContextProvider.Provider>
  );
}

export function useCapabilityRegistryContext(): {
  capabilityRegistry: CapabilityRegistry;
} {
  const context = useContext(CapabilityRegistryContextProvider);

  if (!context) {
    throw new Error(
      'usecapabilityRegistryContext must be used within an capabilityRegistryProvider'
    );
  }

  return { capabilityRegistry: context };
}
